import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X, Phone, ShieldCheck, ArrowRight, Loader2 } from 'lucide-react';
import { useAuth, JUST_AUTHENTICATED_FLAG } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { ConsentBlock, SMS_CONSENT_TEXT } from './consent';
import { formatUSPhoneAsYouType, sanitizeInternationalPhone, toE164 } from '../pages/signupHelpers';

const STEP_PHONE = 'phone';
const STEP_CODE = 'code';

function Backdrop({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in-up">
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8">
        <button onClick={onClose} aria-label="Dismiss"
          className="absolute top-4 right-4 text-brand-green/60 hover:text-brand-green">
          <X className="w-5 h-5" />
        </button>
        {children}
      </div>
    </div>
  );
}

function OptInPhoneStep({ onSent, onDismiss }) {
  const { authAxios } = useAuth();
  const { toast } = useToast();
  const [phone, setPhone] = useState('');
  const [international, setInternational] = useState(false);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  const onPhoneChange = useCallback((e) => {
    const raw = e.target.value;
    setPhone(international ? sanitizeInternationalPhone(raw) : formatUSPhoneAsYouType(raw));
  }, [international]);

  const submit = useCallback(async (e) => {
    e.preventDefault();
    if (!consent) {
      toast({ title: 'Consent required', description: 'Check the box to opt in.', variant: 'destructive' });
      return;
    }
    const digits = phone.replace(/\D/g, '');
    if ((!international && digits.length !== 10) || (international && digits.length < 7)) {
      toast({ title: 'Enter a valid phone number', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      const { data } = await authAxios.post('/auth/sms/opt-in/start', { phone: toE164(phone, international) });
      toast({ title: 'Code sent', description: `We texted a code to ${data.phone_masked}.` });
      onSent(data.phone_masked);
    } catch (err) {
      toast({ title: 'Could not send code', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  }, [phone, international, consent, authAxios, toast, onSent]);

  return (
    <form onSubmit={submit} className="space-y-5">
      <div>
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-brand-gold">
          <ShieldCheck className="w-3.5 h-3.5" /> Optional — not required for account access
        </span>
        <h2 className="mt-3 font-serif text-2xl text-brand-green">Enable SMS on your account</h2>
        <p className="mt-2 text-sm text-brand-green/70">
          Opt in to receive verification codes from LISTING AiGENT. You can skip this and still use your account.
        </p>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label htmlFor="opt-phone" className="text-sm text-brand-green/70">Mobile phone</label>
          <label className="flex items-center gap-2 text-xs text-brand-green/60 cursor-pointer">
            <input type="checkbox" className="accent-brand-green" checked={international}
              onChange={(e) => { setInternational(e.target.checked); setPhone(''); }} />
            International
          </label>
        </div>
        <div className="relative">
          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-green/50 pointer-events-none" />
          <input id="opt-phone" type="tel" required value={phone} onChange={onPhoneChange}
            placeholder={international ? '+44 20 7946 0958' : '(555) 123-4567'}
            className="w-full pl-11 pr-4 py-3 rounded-lg bg-brand-cream border border-brand-cream-dark focus:outline-none focus:ring-2 focus:ring-brand-green/40 text-brand-green" />
        </div>
      </div>

      <div className="p-4 rounded-lg border border-brand-cream-dark bg-brand-cream/60">
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" className="mt-1 w-4 h-4 accent-brand-green shrink-0"
            checked={consent} onChange={(e) => setConsent(e.target.checked)} />
          <span className="text-sm text-brand-green/85 leading-relaxed"><ConsentBlock /></span>
        </label>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <button type="submit" disabled={busy}
          className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-brand-green text-brand-cream font-semibold disabled:opacity-70">
          {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>) : (<>Send verification code <ArrowRight className="w-4 h-4" /></>)}
        </button>
        <button type="button" onClick={onDismiss}
          className="px-5 py-3 rounded-lg border border-brand-green/20 text-brand-green/80 hover:bg-brand-green/5">
          Not now
        </button>
      </div>
    </form>
  );
}

function OptInCodeStep({ phoneMasked, onDone, onCancel }) {
  const { authAxios, setSession, token } = useAuth();
  const { toast } = useToast();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = useCallback(async (e) => {
    e.preventDefault();
    if (code.replace(/\D/g, '').length < 4) return;
    setBusy(true);
    try {
      const { data } = await authAxios.post('/auth/sms/opt-in/confirm', {
        code, consent_text: SMS_CONSENT_TEXT,
      });
      setSession(null, data);
      toast({ title: 'SMS enabled', description: 'You\u2019re opted in. You can opt out any time in Settings.' });
      onDone(data);
    } catch (err) {
      toast({ title: 'Verification failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  }, [code, authAxios, setSession, token, toast, onDone]);

  return (
    <form onSubmit={submit} className="space-y-5">
      <div className="text-center">
        <div className="mx-auto w-12 h-12 rounded-full bg-brand-green flex items-center justify-center mb-3">
          <ShieldCheck className="w-5 h-5 text-brand-cream" />
        </div>
        <h2 className="font-serif text-2xl text-brand-green">Enter your code</h2>
        <p className="mt-2 text-sm text-brand-green/70">Sent to <strong>{phoneMasked}</strong>.</p>
      </div>
      <input type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={10}
        value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} placeholder="123456"
        className="w-full text-center tracking-[0.4em] text-2xl font-serif px-4 py-4 rounded-lg bg-brand-cream border border-brand-cream-dark focus:outline-none focus:ring-2 focus:ring-brand-green/40 text-brand-green" />
      <div className="flex gap-3">
        <button type="submit" disabled={busy}
          className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-brand-green text-brand-cream font-semibold disabled:opacity-70">
          {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Verifying…</>) : 'Verify & opt in'}
        </button>
        <button type="button" onClick={onCancel}
          className="px-5 py-3 rounded-lg border border-brand-green/20 text-brand-green/80 hover:bg-brand-green/5">
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function SmsOptInModal() {
  const { user, authAxios, setSession, token, ready } = useAuth();
  const [step, setStep] = useState(STEP_PHONE);
  const [phoneMasked, setPhoneMasked] = useState('');
  // `armed` becomes true only when the user just authenticated in this tab.
  // It is consumed immediately, so a page refresh will NOT re-open the modal.
  const [armed, setArmed] = useState(false);
  const [dismissedLocally, setDismissedLocally] = useState(false);

  useEffect(() => {
    if (!ready) return;
    if (!user) return;
    if (typeof window === 'undefined') return;
    if (sessionStorage.getItem(JUST_AUTHENTICATED_FLAG)) {
      sessionStorage.removeItem(JUST_AUTHENTICATED_FLAG);
      setArmed(true);
    }
  }, [ready, user]);

  const shouldShow = useMemo(() => {
    if (!ready || !user || !armed || dismissedLocally) return false;
    if (user.role === 'admin') return false;
    // ONLY show when the user has never been asked. Never re-prompt anyone
    // who already opted in, opted out, or previously dismissed.
    return user.sms_opt_in_status === 'unknown';
  }, [ready, user, armed, dismissedLocally]);

  useEffect(() => { if (shouldShow) setStep(STEP_PHONE); }, [shouldShow]);

  const onDismiss = useCallback(async () => {
    setDismissedLocally(true);
    try {
      const { data } = await authAxios.post('/auth/sms/dismiss-modal', {});
      setSession(null, data);
    } catch (err) {
      // Non-fatal — the modal is already closed locally. Surface for debugging.
      // eslint-disable-next-line no-console
      console.warn('sms-dismiss-modal failed (non-fatal):', err?.response?.status || err?.message);
    }
  }, [authAxios, setSession, token]);

  if (!shouldShow) return null;

  return (
    <Backdrop onClose={onDismiss}>
      {step === STEP_PHONE
        ? <OptInPhoneStep onSent={(masked) => { setPhoneMasked(masked); setStep(STEP_CODE); }} onDismiss={onDismiss} />
        : <OptInCodeStep phoneMasked={phoneMasked} onDone={onDismiss} onCancel={() => setStep(STEP_PHONE)} />
      }
    </Backdrop>
  );
}
